/**
 * Team Inbox — SEED canned-message library (30 snippets · 8 categories).
 *
 * Source of truth: TEAMINBOX_SEND_PICKER_MOCKUP.html CANNED[] +
 * AVBP_CANNED_REPLIES_STARTER_LIBRARY.html. English + हिन्दी (Devanagari).
 * Brand names (Aayatan Veda, Clarity Path · Darshan, Griha Gatha) + links
 * stay Latin per the spec. Hindi is AI-drafted — recommend a native-speaker
 * proofread before go-live (see the build report's Pending note).
 *
 * These seed the `settings/wa_canned` library the FIRST time an admin opens
 * the canned manager with an empty store (one-click "Load starter library"),
 * so the picker is populated. They are NOT re-applied if the store is
 * non-empty (never clobbers owner edits).
 */
import type { CannedReply } from './types';

export interface SeedCanned {
  id: string;
  category: string;
  title: string;
  en: string;
  hi: string;
}

export const CANNED_SEED: SeedCanned[] = [
  // ── Greetings & acknowledgement ──
  {
    id: 'GR-01',
    category: 'Greetings & acknowledgement',
    title: 'First response — new enquiry',
    en: "Namaste {{first}} ji 🙏 Thank you for reaching out to Aayatan Veda. We'd be glad to help with your space. May I take a few quick details so I can guide you properly?",
    hi: 'नमस्ते {{first}} जी 🙏 Aayatan Veda से संपर्क करने के लिए धन्यवाद। हमें आपके स्थान में सहायता करके खुशी होगी। सही मार्गदर्शन के लिए क्या मैं कुछ ज़रूरी जानकारी ले लूँ?',
  },
  {
    id: 'GR-02',
    category: 'Greetings & acknowledgement',
    title: 'Acknowledge & hold',
    en: "Thank you, {{first}} ji 🙏 We've received your message and will get back to you shortly with the next step.",
    hi: 'धन्यवाद {{first}} जी 🙏 आपका संदेश हमें मिल गया है। हम शीघ्र ही अगले चरण के साथ आपसे संपर्क करेंगे।',
  },
  {
    id: 'GR-03',
    category: 'Greetings & acknowledgement',
    title: 'After-hours acknowledgement',
    en: "Namaste {{first}} ji 🙏 Thank you for writing in. Our team will respond during working hours (Mon–Sat, 10 am–6 pm). We've noted your message and won't miss it.",
    hi: 'नमस्ते {{first}} जी 🙏 लिखने के लिए धन्यवाद। हमारी टीम कार्य-समय (सोम–शनि, सुबह 10–शाम 6) में उत्तर देगी। आपका संदेश नोट कर लिया गया है, हम इसे नहीं भूलेंगे।',
  },
  // ── Intake & qualification ──
  {
    id: 'IN-01',
    category: 'Intake & qualification',
    title: 'Ask property basics',
    en: 'To guide you well, {{first}} ji, could you share: (1) city & locality, (2) plot size, (3) which direction it faces, and (4) number of floors? Even rough answers are perfectly fine.',
    hi: 'सही मार्गदर्शन के लिए {{first}} जी, क्या आप बता सकते हैं: (1) शहर व क्षेत्र, (2) प्लॉट का आकार, (3) किस दिशा में मुख है, और (4) कितनी मंज़िलें? अनुमान से बताया हुआ भी पूरी तरह ठीक है।',
  },
  {
    id: 'IN-02',
    category: 'Intake & qualification',
    title: 'Send intake form link',
    en: "Here's a short form to capture your space details in one place — it takes about 3 minutes: {{intake_link}} Once it's in, I'll review and revert with the right next step.",
    hi: 'यह एक छोटा फ़ॉर्म है जिसमें आपके स्थान की सारी जानकारी एक जगह आ जाएगी — इसमें लगभग 3 मिनट लगते हैं: {{intake_link}} भरने के बाद मैं देखकर सही अगले चरण के साथ आपसे संपर्क करूँगा।',
  },
  {
    id: 'IN-03',
    category: 'Intake & qualification',
    title: 'Request floor plan / survey / coordinates',
    en: 'Whenever convenient, please share your floor plan or sketch, the plot survey if you have it, and the location pin (Google Maps). These help us read the space accurately.',
    hi: 'जब भी सुविधा हो, कृपया अपना फ़्लोर प्लान या रेखाचित्र, प्लॉट का सर्वे (यदि उपलब्ध हो), और लोकेशन पिन (Google Maps) भेज दीजिए। इनसे हम आपके स्थान को सटीक रूप से समझ पाते हैं।',
  },
  {
    id: 'IN-04',
    category: 'Intake & qualification',
    title: 'Ask facing direction (degrees)',
    en: 'If you can, stand at the main entrance facing out and check the compass on your phone — even an approximate degree reading (e.g. "around 285°") helps us a lot.',
    hi: 'यदि संभव हो तो मुख्य द्वार पर बाहर की ओर मुख करके अपने फ़ोन का कम्पास देखिए — अनुमानित डिग्री (जैसे "लगभग 285°") भी हमारे लिए बहुत सहायक होती है।',
  },
  // ── Service explainers ──
  {
    id: 'SV-01',
    category: 'Service explainers',
    title: 'Clarity Path · Darshan (L1B)',
    en: "Our Clarity Path · Darshan is a focused review of your space — direction-wise reading plus a written remedy checklist you can act on. It's a clear, affordable first step. Shall I share the details and quote for your home?",
    hi: 'हमारा Clarity Path · Darshan आपके स्थान की एक केंद्रित समीक्षा है — दिशा-अनुसार विश्लेषण के साथ एक लिखित उपाय-सूची जिस पर आप अमल कर सकते हैं। यह एक स्पष्ट और किफ़ायती पहला कदम है। क्या मैं आपके घर के लिए विवरण और मूल्य भेज दूँ?',
  },
  {
    id: 'SV-02',
    category: 'Service explainers',
    title: 'Alignment Path · Nidana (L2B)',
    en: 'Our Alignment Path · Nidana is a detailed, zone-by-zone analysis — entrance & energy assessment, room-wise reading and a full corrective plan. Best when you want depth before building or renovating. Would you like the scope and quote?',
    hi: 'हमारा Alignment Path · Nidana एक विस्तृत, ज़ोन-दर-ज़ोन विश्लेषण है — प्रवेश व ऊर्जा का आकलन, कक्ष-अनुसार विश्लेषण और एक संपूर्ण सुधार-योजना। निर्माण या नवीनीकरण से पहले गहराई चाहिए तो यह सर्वोत्तम है। क्या आप स्कोप और मूल्य चाहेंगे?',
  },
  {
    id: 'SV-03',
    category: 'Service explainers',
    title: '3D photo-realistic view',
    en: "We also prepare photo-realistic 3D elevation views of your home — so you can see the design before it's built. Usually 1–2 views covering the floors plus the staircase. Want a quote for yours?",
    hi: 'हम आपके घर के photo-realistic 3D एलिवेशन व्यू भी तैयार करते हैं — ताकि बनने से पहले आप डिज़ाइन देख सकें। आमतौर पर 1–2 व्यू, मंज़िलों और सीढ़ी सहित। क्या आपके लिए मूल्य भेज दूँ?',
  },
  {
    id: 'SV-04',
    category: 'Service explainers',
    title: 'Which is right for you',
    en: "If you'd like a quick, clear direction-check, the Clarity Path is ideal. If you're planning construction or renovation and want a complete corrective plan, the Alignment Path fits better. Tell me your situation and I'll suggest the right one — no pressure.",
    hi: 'यदि आपको शीघ्र, स्पष्ट दिशा-जाँच चाहिए तो Clarity Path उपयुक्त है। यदि आप निर्माण या नवीनीकरण की योजना बना रहे हैं और संपूर्ण सुधार-योजना चाहते हैं तो Alignment Path अधिक उपयुक्त है। अपनी स्थिति बताइए, मैं सही विकल्प सुझा दूँगा — कोई दबाव नहीं।',
  },
  {
    id: 'SV-05',
    category: 'Service explainers',
    title: 'Timeline / turnaround',
    en: "Once we have your details and the advance is in, your {{service}} is typically ready within the agreed timeline we'll confirm with your quote. We'll keep you updated at each step.",
    hi: 'आपकी जानकारी और अग्रिम राशि मिलते ही, आपका {{service}} आमतौर पर उस समय-सीमा में तैयार हो जाता है जो हम मूल्य के साथ पुष्टि करेंगे। हर चरण पर हम आपको अवगत कराते रहेंगे।',
  },
  // ── Quote & payment ──
  {
    id: 'QP-02',
    category: 'Quote & payment',
    title: 'Payment details (UPI / QR / bank)',
    en: 'Here are the payment details, {{first}} ji. Your payment is your acceptance of the quotation and we begin right away.\nUPI: {{upi}} · or scan the QR (attached)\nBank: {{bank_name}} · {{bank_acno}} · {{bank_ifsc}}\nDo share a screenshot once done, so we can confirm quickly.',
    hi: 'यह रहे भुगतान विवरण, {{first}} जी। आपका भुगतान ही quotation की स्वीकृति है और हम तुरंत कार्य आरंभ कर देते हैं।\nUPI: {{upi}} · या QR स्कैन कीजिए (संलग्न)\nBank: {{bank_name}} · {{bank_acno}} · {{bank_ifsc}}\nहो जाने पर कृपया स्क्रीनशॉट भेज दीजिए, ताकि हम शीघ्र पुष्टि कर सकें।',
  },
  {
    id: 'QP-03',
    category: 'Quote & payment',
    title: 'Gentle payment reminder',
    en: "Just a gentle note, {{first}} ji 🙏 Your quote for {{service}} is ready whenever you'd like to proceed. Happy to answer anything before you do.",
    hi: 'बस एक स्नेहपूर्ण स्मरण, {{first}} जी 🙏 {{service}} के लिए आपका मूल्य-पत्र तैयार है, जब भी आप आगे बढ़ना चाहें। उससे पहले कुछ भी पूछना हो तो खुशी से बताएँगे।',
  },
  {
    id: 'QP-04',
    category: 'Quote & payment',
    title: 'Payment received → next steps',
    en: "Received with thanks, {{first}} ji 🙏 Your {{service}} is now confirmed and we've begun. Here's what happens next: we'll review your details and revert with the first step / any inputs we need.",
    hi: 'धन्यवाद सहित प्राप्त हुआ, {{first}} जी 🙏 आपका {{service}} अब पुष्ट हो गया है और हमने कार्य आरंभ कर दिया है। आगे यह होगा: हम आपकी जानकारी की समीक्षा करके पहले चरण / आवश्यक इनपुट के साथ संपर्क करेंगे।',
  },
  {
    id: 'QP-05',
    category: 'Quote & payment',
    title: 'GST invoice clarification',
    en: 'On billing, {{first}} ji — for a GST invoice we bill under Aayatan Veda; for our GST-exempt rate we bill under Rohit Khandelwal (HUF). Tell us which you need and we\'ll set the quote accordingly.',
    hi: 'बिलिंग के संबंध में, {{first}} जी — GST इनवॉइस के लिए हम Aayatan Veda के अंतर्गत बिल करते हैं; GST-मुक्त दर के लिए Rohit Khandelwal (HUF) के अंतर्गत। आपको कौन-सा चाहिए बताइए, हम मूल्य उसी अनुसार तय कर देंगे।',
  },
  // ── Scheduling ──
  {
    id: 'SC-01',
    category: 'Scheduling',
    title: 'Propose a call slot',
    en: "Happy to talk it through, {{first}} ji. Would {{date}} at {{time}} suit you for a short call? If not, share a time that works and we'll fit around you.",
    hi: 'बात करके समझाने में खुशी होगी, {{first}} जी। क्या {{date}} को {{time}} बजे एक छोटी कॉल आपके लिए ठीक रहेगी? यदि नहीं, तो अपना सुविधाजनक समय बता दीजिए, हम उसके अनुसार समायोजित कर लेंगे।',
  },
  {
    id: 'SC-02',
    category: 'Scheduling',
    title: 'Confirm appointment',
    en: "Confirmed, {{first}} ji 🙏 We'll connect on {{date}} at {{time}}. I'll call you on this number. See you then!",
    hi: 'पुष्टि हो गई, {{first}} जी 🙏 हम {{date}} को {{time}} बजे जुड़ेंगे। मैं इसी नंबर पर कॉल करूँगा। तब मिलते हैं!',
  },
  {
    id: 'SC-03',
    category: 'Scheduling',
    title: 'Site-visit coordination',
    en: "For a site visit, {{first}} ji, please share the full address with a Maps pin and a couple of dates that suit you. We'll confirm the visit and the team coming.",
    hi: 'साइट विज़िट के लिए, {{first}} जी, कृपया पूरा पता Maps पिन सहित और दो-तीन सुविधाजनक तिथियाँ भेज दीजिए। हम विज़िट और आने वाली टीम की पुष्टि कर देंगे।',
  },
  {
    id: 'SC-04',
    category: 'Scheduling',
    title: 'Reschedule',
    en: "No problem at all, {{first}} ji. Let's find a better time — what day and time would suit you this week?",
    hi: 'बिल्कुल कोई बात नहीं, {{first}} जी। बेहतर समय निकालते हैं — इस सप्ताह कौन-सा दिन और समय आपके लिए ठीक रहेगा?',
  },
  // ── During delivery ──
  {
    id: 'DL-01',
    category: 'During delivery',
    title: 'Work has begun',
    en: "We've started on your {{service}}, {{first}} ji 🙏 Our team is reviewing your space now. We'll reach out if we need anything and keep you posted on progress.",
    hi: 'हमने आपके {{service}} पर कार्य आरंभ कर दिया है, {{first}} जी 🙏 हमारी टीम अभी आपके स्थान की समीक्षा कर रही है। कुछ आवश्यकता होगी तो संपर्क करेंगे और प्रगति से अवगत कराते रहेंगे।',
  },
  {
    id: 'DL-02',
    category: 'During delivery',
    title: 'Request missing inputs',
    en: 'To move forward smoothly, {{first}} ji, could you send: a few clear photos of the entrance and main rooms, and any measurements you have? It helps us finish accurately.',
    hi: 'सुचारू रूप से आगे बढ़ने के लिए, {{first}} जी, क्या आप भेज सकते हैं: प्रवेश और मुख्य कक्षों की कुछ स्पष्ट तस्वीरें, और जो भी माप उपलब्ध हों? इससे हम कार्य सटीकता से पूरा कर पाते हैं।',
  },
  {
    id: 'DL-03',
    category: 'During delivery',
    title: 'Deliverable ready',
    en: "Good news, {{first}} ji 🙏 Your {{service}} is ready. I'm sharing it now. Do go through it, and we'll set up a short call to walk you through anything you'd like.",
    hi: 'खुशखबरी, {{first}} जी 🙏 आपका {{service}} तैयार है। मैं इसे अभी साझा कर रहा हूँ। कृपया इसे देख लीजिए, और जो भी समझाना हो उसके लिए हम एक छोटी कॉल तय कर लेंगे।',
  },
  {
    id: 'DL-04',
    category: 'During delivery',
    title: 'Day-44 remedy call',
    en: "As we'd planned, {{first}} ji, it's time for your follow-up call to walk through the remedies and answer how things are going. Would {{date}} at {{time}} work?",
    hi: 'जैसा हमने तय किया था, {{first}} जी, आपकी फ़ॉलो-अप कॉल का समय आ गया है — उपायों को समझाने और हाल-चाल जानने के लिए। क्या {{date}} को {{time}} ठीक रहेगा?',
  },
  // ── Post-delivery & nurture ──
  {
    id: 'PD-01',
    category: 'Post-delivery & nurture',
    title: 'Thank you + feedback (Griha Gatha)',
    en: "It was a pleasure working with you, {{first}} ji 🙏 If you're open to it, we'd love to feature your experience on Griha Gatha, our client stories. No pressure at all — only if it feels right to you.",
    hi: 'आपके साथ कार्य करके बहुत अच्छा लगा, {{first}} जी 🙏 यदि आप सहमत हों, तो हम आपके अनुभव को Griha Gatha — हमारी क्लाइंट कहानियों — में सम्मिलित करना चाहेंगे। कोई दबाव नहीं, केवल तभी जब आपको उचित लगे।',
  },
  {
    id: 'PD-02',
    category: 'Post-delivery & nurture',
    title: 'Referral ask',
    en: "Thank you for your trust, {{first}} ji 🙏 If anyone you know is building, renovating, or simply wants their space to feel right, we'd be honoured by your referral.",
    hi: 'आपके विश्वास के लिए धन्यवाद, {{first}} जी 🙏 यदि आपके परिचित में कोई निर्माण या नवीनीकरण कर रहा हो, या बस अपने स्थान को सही अनुभव कराना चाहता हो — तो आपका संदर्भ (referral) हमारे लिए सम्मान की बात होगी।',
  },
  {
    id: 'PD-03',
    category: 'Post-delivery & nurture',
    title: 'Remedy check-in',
    en: 'Namaste {{first}} ji 🙏 Just checking in — were you able to put the suggested changes in place, and how is the space feeling? Happy to help with anything that\'s unclear.',
    hi: 'नमस्ते {{first}} जी 🙏 बस हाल जानने के लिए — क्या आप सुझाए गए बदलाव कर पाए, और स्थान कैसा अनुभव हो रहा है? कुछ भी अस्पष्ट हो तो सहायता के लिए तैयार हैं।',
  },
  // ── FAQ & objection handling ──
  {
    id: 'FQ-01',
    category: 'FAQ & objection handling',
    title: 'Remote vs on-site',
    en: 'Great question, {{first}} ji. Most of our work is done remotely using your plan, photos and orientation — accurate and convenient. An on-site visit is optional and arranged only when the project genuinely needs it.',
    hi: 'बहुत अच्छा प्रश्न, {{first}} जी। हमारा अधिकांश कार्य आपके प्लान, तस्वीरों और दिशा के आधार पर दूर से ही हो जाता है — सटीक और सुविधाजनक। साइट विज़िट वैकल्पिक है, केवल तभी जब प्रोजेक्ट को सचमुच आवश्यकता हो।',
  },
  {
    id: 'FQ-02',
    category: 'FAQ & objection handling',
    title: 'Do you break / demolish structures?',
    en: "Please don't worry, {{first}} ji 🙏 Our approach favours practical, non-structural corrections wherever possible — we avoid breaking or demolishing. The aim is balance with minimal disruption to your home.",
    hi: 'कृपया चिंता न करें, {{first}} जी 🙏 हमारा तरीका जहाँ तक संभव हो व्यावहारिक, बिना तोड़-फोड़ के सुधार पर आधारित होता है — तोड़ना या गिराना हम टालते हैं। लक्ष्य है संतुलन, आपके घर में कम से कम व्यवधान के साथ।',
  },
  {
    id: 'FQ-03',
    category: 'FAQ & objection handling',
    title: 'Pricing / value',
    en: "Fair question, {{first}} ji. The fee reflects a careful, personalised study of your specific space and a clear plan you can act on — not a generic checklist. We've kept the entry option affordable so you can start with confidence.",
    hi: 'उचित प्रश्न, {{first}} जी। यह शुल्क आपके विशिष्ट स्थान के सावधानीपूर्वक, व्यक्तिगत अध्ययन और एक स्पष्ट, अमल-योग्य योजना को दर्शाता है — कोई सामान्य चेकलिस्ट नहीं। प्रवेश-विकल्प हमने किफ़ायती रखा है ताकि आप विश्वास के साथ शुरुआत कर सकें।',
  },
  {
    id: 'FQ-04',
    category: 'FAQ & objection handling',
    title: 'Polite decline / out-of-area (softened)',
    en: "Thank you for thinking of us, {{first}} ji 🙏 This particular request may sit a little outside what we can take on right now — but let us take a closer look before we say anything firm. We'll arrange a quick callback so nothing is left unresolved.",
    hi: 'हमें याद रखने के लिए धन्यवाद, {{first}} जी 🙏 यह विशेष अनुरोध शायद अभी हमारे दायरे से थोड़ा बाहर हो — पर कुछ निश्चित कहने से पहले हम इसे ध्यान से देख लेते हैं। हम एक शीघ्र कॉलबैक की व्यवस्था कर देंगे ताकि कोई बात अधूरी न रहे।',
  },
];

/** The 8 seed categories in display order. */
export const CANNED_CATEGORIES: string[] = Array.from(
  CANNED_SEED.reduce((set, s) => set.add(s.category), new Set<string>()),
);

/** PURE — a SeedCanned → a full CannedReply (shared visibility, vars detected). */
export function seedToCanned(
  s: SeedCanned,
  detectVars: (en: string, hi: string) => string[],
): CannedReply {
  return {
    id: s.id,
    slash: `/${s.id.toLowerCase()}`,
    title: s.title,
    category: s.category,
    text: s.en,
    textEn: s.en,
    textHi: s.hi,
    vars: detectVars(s.en, s.hi),
    visibility: 'shared',
    ownerUid: null,
    numberScope: null,
  };
}
